import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { fetchWithAuth } from '../../utils/api';
import { Edit, X } from 'lucide-react';
import RowsPerPageSelector from '../common/RowsPerPageSelector';
import Pagination from '../common/Pagination';
import ConfirmationModal from '../modals/ConfirmationModal';

function Locations({ user, handleLogout }) {
  const [locations, setLocations] = useState([]);
  const [newLocation, setNewLocation] = useState('');
  const [error, setError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, id: null });
  const [editingLocation, setEditingLocation] = useState(null);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });

  const fetchLocations = useCallback(() => {
    fetchWithAuth('/locations', {}, user, handleLogout)
      .then(res => res.json())
      .then(data => setLocations(data || []))
      .catch(err => console.error(err));
  }, [user, handleLogout]);

  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  useEffect(() => {
    setCurrentPage(1);
  }, [rowsPerPage, sortConfig]);

  const sortedLocations = useMemo(() => {
    let sortableItems = [...locations];
    if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];

        if (typeof aVal === 'string') {
          aVal = aVal.toLowerCase();
          bVal = bVal.toLowerCase();
        }

        if (aVal < bVal) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aVal > bVal) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [locations, sortConfig]);

  const paginatedLocations = useMemo(() => {
    if (rowsPerPage === 'all') return sortedLocations;
    const start = (currentPage - 1) * rowsPerPage;
    return sortedLocations.slice(start, start + rowsPerPage);
  }, [sortedLocations, currentPage, rowsPerPage]);

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handleCreate = (e) => {
    e.preventDefault();
    setError('');
    fetchWithAuth('/locations/create', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: newLocation })
    }, user, handleLogout)
    .then(async res => {
      if (!res.ok) throw new Error(await res.text());
      fetchLocations();
      setNewLocation('');
    })
    .catch(err => setError(err.message));
  };

  const handleEditClick = (loc) => {
    setEditingLocation(loc);
    setError('');
  };

  const handleUpdateSubmit = (e) => {
    e.preventDefault();
    setError('');
    fetchWithAuth('/locations/update', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(editingLocation)
    }, user, handleLogout)
    .then(async res => {
      if (!res.ok) throw new Error(await res.text());
      fetchLocations();
      setEditingLocation(null);
    })
    .catch(err => setError(err.message));
  };

  const handleDelete = (id) => {
    setDeleteConfirm({ isOpen: true, id });
  };

  const confirmDelete = () => {
    const id = deleteConfirm.id;
    fetchWithAuth(`/locations/delete?id=${id}`, {
      method: 'POST'
    }, user, handleLogout)
    .then(async res => {
      if (!res.ok) throw new Error(await res.text());
      fetchLocations();
      setDeleteConfirm({ isOpen: false, id: null });
    })
    .catch(err => {
      setError(err.message);
      setDeleteConfirm({ isOpen: false, id: null });
    });
  };

  return (
    <div className="locations-view">
      <div className="view-header">
        <h2>Locations</h2>
      </div>

      {error && <div className="error-message">{error}</div>}
      
      <div className="list-controls">
        <div className="control-group">
          <RowsPerPageSelector value={rowsPerPage} onChange={setRowsPerPage} />
        </div>
      </div>

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th onClick={() => requestSort('id')} className="sortable">ID {sortConfig.key === 'id' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</th>
              <th onClick={() => requestSort('name')} className="sortable">Name {sortConfig.key === 'name' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedLocations.map(l => (
              <tr key={l.id}>
                <td>{l.id}</td>
                <td>{l.name}</td>
                <td className="row-actions">
                  <button className="action-btn sm" onClick={() => handleEditClick(l)}>Edit</button>
                  <button className="cancel-btn sm" onClick={() => handleDelete(l.id)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination 
        totalItems={locations.length} 
        itemsPerPage={rowsPerPage} 
        currentPage={currentPage} 
        onPageChange={setCurrentPage} 
      />

      <div className="form-section">
        <h3>Add New Location</h3>
        <form onSubmit={handleCreate} className="compact-form">
          <div className="form-group">
            <label>Location Name</label>
            <input value={newLocation} onChange={e => setNewLocation(e.target.value)} placeholder="e.g. Building A" required />
          </div>
          <div className="form-actions">
            <button type="submit" className="submit-btn">Add Location</button>
          </div>
        </form>
      </div>

      <ConfirmationModal 
        isOpen={deleteConfirm.isOpen}
        title="Delete Location"
        message="Are you sure you want to delete this location? This action cannot be undone."
        onConfirm={confirmDelete}
        onCancel={() => setDeleteConfirm({ isOpen: false, id: null })}
      />

      {editingLocation && (
        <div className="modal" onClick={() => { setEditingLocation(null); setError(''); }}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title-group">
                <Edit size={24} />
                <h3>Edit Location</h3>
              </div>
              <button className="close-btn" onClick={() => setEditingLocation(null)}><X size={20} /></button>
            </div>
            {error && <div className="error-message">{error}</div>}
            <form onSubmit={handleUpdateSubmit}>
              <div className="form-group">
                <label>Location Name</label>
                <input 
                  value={editingLocation.name} 
                  onChange={e => setEditingLocation({...editingLocation, name: e.target.value})} 
                  required 
                />
              </div>
              <div className="form-actions">
                <button type="submit" className="submit-btn">Update</button>
                <button type="button" className="secondary-btn" onClick={() => { setEditingLocation(null); setError(''); }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Locations;
